import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PRESS_OUTREACH_REPLIES_OPENED_EVENT,
  countUnreadPressOutreachReplies,
  getPressOutreachRepliesLastOpenedAt,
  setPressOutreachRepliesLastOpenedAt,
  type PressOutreachReplyForUnread,
} from '@/lib/pressOutreachRepliesStorage';

export function usePressOutreachUnreadReplies(
  replies: PressOutreachReplyForUnread[] | undefined,
) {
  const [lastOpenedAt, setLastOpenedAt] = useState<string | null>(() =>
    getPressOutreachRepliesLastOpenedAt(),
  );

  useEffect(() => {
    const onOpened = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail;
      setLastOpenedAt(detail ?? getPressOutreachRepliesLastOpenedAt());
    };
    window.addEventListener(PRESS_OUTREACH_REPLIES_OPENED_EVENT, onOpened);
    return () => {
      window.removeEventListener(PRESS_OUTREACH_REPLIES_OPENED_EVENT, onOpened);
    };
  }, []);

  const unreadCount = useMemo(
    () => countUnreadPressOutreachReplies(replies ?? [], lastOpenedAt),
    [replies, lastOpenedAt],
  );

  const markRepliesOpened = useCallback(() => {
    const at = new Date().toISOString();
    setPressOutreachRepliesLastOpenedAt(at);
    setLastOpenedAt(at);
  }, []);

  return {
    unreadCount,
    hasUnread: unreadCount > 0,
    lastOpenedAt,
    markRepliesOpened,
  };
}
